import { useGetAttachmentDocumentTypeList } from "@/api/services/global/master";
import { ResolvedDocumentTag } from "@/features/talent/components/talent-detail/DocumentTypeTagsReadOnly";
import { ATTACHED_DOCUMENT_COLOR } from "@/types/enum";
import { useMemo } from "react";

/**
 * Resolves attachmentDocumentTypeIds → ResolvedDocumentTag[] consumed by `<DocumentTypeTagsReadOnly>`.
 * Document-type list is fetched once and the map is memoized so callers can invoke the returned
 * function per file row without rebuilding lookup state.
 */
export const useResolveDocumentTags = () => {
  const { data: documentTypes } = useGetAttachmentDocumentTypeList();

  return useMemo(() => {
    const map = new Map<number, { color: string; documentTypeName: string }>();
    documentTypes?.forEach((dt) => map.set(Number(dt.id), { color: dt.color, documentTypeName: dt.documentTypeName }));

    return (typeIds?: number[] | null): ResolvedDocumentTag[] => {
      if (!typeIds) return [];
      return typeIds
        .map((typeId): ResolvedDocumentTag | null => {
          const info = map.get(typeId);
          if (!info) return null;
          const colorPair = ATTACHED_DOCUMENT_COLOR[info.color as keyof typeof ATTACHED_DOCUMENT_COLOR];
          const bgColor = colorPair?.bgColor || "#fff";
          const color = colorPair?.color || "#000000";
          return {
            id: typeId,
            label: info.documentTypeName,
            bgColor,
            color,
            hasBorder: bgColor === "#FFFFFF",
          };
        })
        .filter((tag): tag is ResolvedDocumentTag => tag !== null);
    };
  }, [documentTypes]);
};
